/** @type {number} Skew angle in degrees */
export const SKEW_ANGLE = 55;

/** @type {number} Base pressure in millibars */
export const BASE_PRESSURE = 1050;

/** @type {number} Top pressure in millibars */
export const TOP_PRESSURE = 100;

/** @type {number[]} Labeled pressure lines */
export const PRESSURE_LINES = [1000, 850, 700, 500, 300, 200, 100];

/** @type {number[]} Tick-only pressure lines */
export const PRESSURE_TICKS = [950, 900, 800, 750, 650, 600, 550, 450, 400, 350, 250, 150];

/** @type {number} Wind barb stem length in px */
export const BARB_SIZE = 25;

/** @type {number} Degrees-to-radians conversion factor */
export const DEG2RAD = Math.PI / 180;

/** @type {number} Tangent of the skew angle */
export const TAN = Math.tan(SKEW_ANGLE * DEG2RAD);

/** @type {number} Sentinel value for missing temperature data */
export const MISSING_TEMP = -1000;

/** @type {number} Kelvin offset */
export const KELVIN = 273.15;

/** @type {number} Poisson exponent for dry adiabats */
export const POISSON = 0.286;

/** @type {{top: number, right: number, bottom: number, left: number}} Default
 * (desktop) margins. Match Skysight's responsive scheme: wider charts (>500px)
 * use this set; narrower charts use MOBILE_MARGIN below. */
export const DEFAULT_MARGIN = Object.freeze({ top: 50, right: 35, bottom: 30, left: 70 });

/** Tighter margins used for charts where the available width is ≤500px. */
export const MOBILE_MARGIN = Object.freeze({ top: 45, right: 30, bottom: 25, left: 60 });

/** Width threshold (px) at which we switch between DEFAULT_MARGIN and MOBILE_MARGIN. */
export const MOBILE_MARGIN_WIDTH = 500;

/** @type {[number, number]} Temperature domain in °C */
export const TEMP_DOMAIN = [-45, 50];

// ===== Skysight-compatible piecewise skew (modifications) =====
// Skysight uses a piecewise-linear skew with two angles, joined at 500 hPa.
// Source: docs/skysight-skewt-bundle.js (this.tanBottom, this.tanTop,
// this.breakingP, this.breakingFraction).
//
// The skew offset for a point (T, p) becomes:
//   bottom (p >= 500): offset = (y(basep) - y(p)) / TAN_BOTTOM
//   top    (p <  500): offset = (y(basep) - y(500)) / TAN_BOTTOM
//                              + (y(500)   - y(p))   / TAN_TOP

/** Tangent of the lower-portion skew angle. Source: Skysight bundle. */
export const TAN_BOTTOM = Math.tan(60 * DEG2RAD);

/** Tangent of the upper-portion skew angle. Source: Skysight bundle. */
export const TAN_TOP = Math.tan(20 * DEG2RAD);

/** Pressure at which the skew switches from TAN_BOTTOM to TAN_TOP. */
export const BREAKING_PRESSURE = 500;

/**
 * Fraction of total chart height allocated to the UPPER (low-pressure /
 * stratospheric) portion, i.e. [BREAKING_PRESSURE → TOP_PRESSURE].
 * 0.25 means the top 25% of pixels span that range; the remaining 75%
 * span [BASE_PRESSURE → BREAKING_PRESSURE] (the troposphere, where pilots
 * fly). Matches Skysight bundle naming: `hTop = h * breakingFraction`.
 */
export const BREAKING_FRACTION = 0.25;
